// Wave 20.20 — Peaks panel.
//
// Renders GET /activities/:id/peaks (server returns per-second
// mean-maximal curves + top-5 records + session-total PRs).
// Shape is the same for outdoor rides/runs and indoor aero
// sessions — the aero session just also has an aeroPeaks tab.
//
// Layout (option "b" — full curve as the hero, top-5 records
// list below):
//
//   ┌───────────────────────────────────────────────┐
//   │  Peaks                                        │
//   │  ┌────────┐┌──────┐┌──────┐  tab picker      │
//   │  │ Power  ││ Pace ││ Aero │                  │
//   │  └────────┘└──────┘└──────┘                  │
//   │                                                │
//   │  <mean-maximal chart, log-x>                   │
//   │                                                │
//   │  Top records                                   │
//   │  ┌────────────────────────────────────────┐   │
//   │  │ 1:00  380 W  · 22.4 km/h  [PR +12 W]   │   │
//   │  │ 5:00  320 W  · 20.1 km/h  [PR +8 W]    │   │
//   │  │ …                                       │   │
//   │  └────────────────────────────────────────┘   │
//   │                                                │
//   │  Session totals                                │
//   │  Distance 42.3 km  Elev 512 m  [PR]  …         │
//   └───────────────────────────────────────────────┘

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import IosShareIcon from '@mui/icons-material/IosShare';
import CheckIcon from '@mui/icons-material/Check';
import { LineChart } from '@mui/x-charts/LineChart';
import { colors } from '../theme';
import { fmtDistance, fmtElevation, fmtPace, fmtPeakDistance, fmtSpeed, useUnitSystem } from '../services/useUnitSystem';

const API = process.env.REACT_APP_API || 'https://api.aero.chat';
const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';

interface CurveEntry {
  duration_sec: number;
  start_sec: number;
  end_sec: number;
  avg_power_w: number | null;
  avg_speed_mps: number | null;
  avg_pace_s_per_m: number | null;
  distance_m: number;
}

interface AeroEntry {
  duration_sec: number;
  start_sec: number;
  end_sec: number;
  avg_power_w: number;
  avg_cda: number;
  predicted_speed_mps: number;
  predicted_distance_m: number;
}

interface TopRecord {
  duration_sec: number;
  value: number;
  prior_best: number;
  is_pr: boolean;
  entry: any;
}

interface PeaksResponse {
  powerPeaks: CurveEntry[];
  pacePeaks: CurveEntry[];
  aeroPeaks: AeroEntry[];
  sessionTotals: {
    distanceM: number | null;
    elevationGainM: number | null;
    avgSpeedMps: number | null;
    durationSec: number | null;
  };
  prs: {
    distanceM: boolean;
    elevationGainM: boolean;
    avgSpeedMps: boolean;
    durationSec: boolean;
  };
  topRecords: {
    power: TopRecord[];
    pace: TopRecord[];
    aero: TopRecord[];
  };
}

interface Props {
  activityId: string;
  /** Wave 20.27 — When set, per-peak drop buttons hand the drop
   *  intent to the parent (which opens the unified DropLinkDialog).
   *  When not set, the panel falls back to auto-minting with default
   *  window/visibility so existing embedders keep working. */
  onRequestPeakDrop?: (payload: {
    kind: string;
    value: number;
    unit: string;
    contextLabel: string;
    // Wave 20.31 — effort window (either or both may be null).
    durationSec?: number | null;
    distanceM?: number | null;
  }) => void;
  /** Wave 20.32 — a peak row was tapped. Parent typically maps this
   *  into a highlight on the route map + elevation profile. Passing
   *  null means "clear the selection". */
  onSelectPeak?: (peak: {
    kind: string;
    startSec: number;
    endSec: number;
    valueLabel: string;
    durationLabel: string;
    distanceLabel: string | null;
  } | null) => void;
  /** Wave 20.32 — the currently-selected peak (for row highlighting).
   *  Same key as the row's `${kind}:${startSec}:${endSec}`. */
  selectedPeakKey?: string | null;
  /** Set to true on indoor aero session pages so the Aero tab is
   *  the default. Outdoor pages default to Power (or Pace if no
   *  power data). */
  isAeroSession?: boolean;
  /** Wave 20.49y — activity type. Drives unit formatting for
   *  `sec_per_m` peaks: running renders as min/mi (min/km);
   *  cycling renders as mph (kph) per rider preference "for
   *  cycling, all detected peaks for pace should be in mph or
   *  kph." Undefined defaults to speed (safer for the cycling-
   *  dominant case). */
  activityType?: 'indoor_aero' | 'outdoor_cycling' | 'outdoor_running';
}

// Wave 20.25 — state-machine peak rows (from /activities/:id/intervals).
// One row per detected effort. Empty list is a valid answer (no peaks
// sustained above the threshold this session).
interface IntervalRow {
  kind: string;
  value: number;
  unit: 'W' | 'mps' | 'sec_per_m' | 'sec' | 'm' | 'm_per_h';
  startSec: number;
  endSec: number;
  distanceM: number | null;
}

type TabKey = 'power' | 'pace' | 'aero';

export default function PeaksPanel({ activityId, isAeroSession, activityType, onRequestPeakDrop, onSelectPeak, selectedPeakKey }: Props) {
  const isRun = activityType === 'outdoor_running';
  const [data, setData] = useState<PeaksResponse | null>(null);
  const [intervals, setIntervals] = useState<IntervalRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey | null>(null);
  const [snack, setSnack] = useState<string | null>(null);

  useEffect(() => {
    const jwt = localStorage.getItem(TOKEN_KEY);
    fetch(`${API}/activities/${activityId}/peaks`, {
      headers: { Authorization: `Bearer ${jwt}` },
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(`${r.status}`);
        setData(await r.json());
      })
      .catch((e) => setError(e?.message || 'Failed to load'));
    // Wave 20.25 — state-machine peaks. Fetched in parallel; the list
    // under each tab renders these instead of the fixed-duration
    // Top-Records the mean-maximal curve used to produce.
    fetch(`${API}/activities/${activityId}/intervals`, {
      headers: { Authorization: `Bearer ${jwt}` },
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((rows: IntervalRow[]) => setIntervals(Array.isArray(rows) ? rows : []))
      .catch(() => {
        /* non-fatal — chart still renders */
      });
  }, [activityId]);

  // Default tab: pick the first non-empty in a sensible order.
  useEffect(() => {
    if (!data || tab) return;
    const order: TabKey[] = isAeroSession
      ? ['aero', 'power', 'pace']
      : ['power', 'pace', 'aero'];
    for (const k of order) {
      const curve = k === 'power' ? data.powerPeaks : k === 'pace' ? data.pacePeaks : data.aeroPeaks;
      if (curve && curve.length > 0) {
        setTab(k);
        return;
      }
    }
  }, [data, tab, isAeroSession]);

  if (error) return <ErrorCard message={error} />;
  if (!data) return <LoadingCard />;

  const anyData =
    data.powerPeaks.length > 0 || data.pacePeaks.length > 0 || data.aeroPeaks.length > 0;
  if (!anyData) return <EmptyCard />;

  const activeTab: TabKey = tab ?? (isAeroSession ? 'aero' : 'power');

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: 3,
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
      }}
    >
      <Typography variant="overline" sx={{ color: colors.brand, letterSpacing: '0.2em' }}>
        Peaks
      </Typography>
      <Tabs
        value={activeTab}
        onChange={(_, v) => setTab(v as TabKey)}
        sx={{
          mt: 0.5,
          mb: 2,
          minHeight: 36,
          borderBottom: `1px solid ${colors.border}`,
          '& .MuiTab-root': {
            color: colors.textMuted,
            textTransform: 'none',
            fontWeight: 700,
            minHeight: 36,
            py: 1,
          },
          '& .Mui-selected': { color: `${colors.brand} !important` },
          '& .MuiTabs-indicator': { backgroundColor: colors.brand },
        }}
      >
        <Tab
          value="power"
          label="Power"
          disabled={data.powerPeaks.length === 0}
        />
        <Tab
          value="pace"
          label="Pace"
          disabled={data.pacePeaks.length === 0}
        />
        <Tab
          value="aero"
          label="Aero"
          disabled={data.aeroPeaks.length === 0}
        />
      </Tabs>

      {/* Wave 20.38 — Dropped the per-second mean-maximal Power /
          Pace / Aero curve charts and the SessionTotals footer per
          user request. Peaks list stands on its own; the summary
          card at the top of ActivityDetail already carries the
          totals + PRs. Aero tab retained for indoor aero sessions
          since it shows the CdA-vs-time curve, not a mean-maximal
          plot. */}
      {activeTab === 'power' && (
        <StateMachinePeaks
          rows={intervals.filter(
            (i) =>
              i.kind.startsWith('peak_power') ||
              (i.kind === 'peak_whole' && i.unit === 'W'),
          )}
          isRun={isRun}
          onDropped={(msg) => setSnack(msg)}
          onRequestPeakDrop={onRequestPeakDrop}
          onSelectPeak={onSelectPeak}
          selectedPeakKey={selectedPeakKey}
        />
      )}
      {activeTab === 'pace' && (
        <StateMachinePeaks
          rows={intervals.filter(
            (i) =>
              i.kind.startsWith('peak_pace') ||
              i.kind.startsWith('peak_speed') ||
              i.kind === 'peak_gap' ||
              i.kind === 'peak_vam' ||
              i.kind === 'peak_grade_adj_speed' ||
              i.kind === 'peak_grade_adj_pace' ||
              (i.kind === 'peak_whole' && (i.unit === 'mps' || i.unit === 'sec_per_m')),
          )}
          isRun={isRun}
          onDropped={(msg) => setSnack(msg)}
          onRequestPeakDrop={onRequestPeakDrop}
          onSelectPeak={onSelectPeak}
          selectedPeakKey={selectedPeakKey}
        />
      )}
      {activeTab === 'aero' && (
        <AeroView curve={data.aeroPeaks} top={data.topRecords.aero} />
      )}

      <Snackbar
        open={!!snack}
        autoHideDuration={3500}
        onClose={() => setSnack(null)}
        message={snack ?? ''}
      />
    </Box>
  );
}

// Wave 20.25 — State-machine peaks: one row per detected effort with
// its actual duration + distance + value. Icon-only drop button on
// each row that mints /peaks/share and copies the URL to clipboard.
function StateMachinePeaks({
  rows,
  isRun,
  onDropped,
  onRequestPeakDrop,
  onSelectPeak,
  selectedPeakKey,
}: {
  rows: IntervalRow[];
  isRun: boolean;
  onDropped: (msg: string) => void;
  onRequestPeakDrop?: (payload: {
    kind: string;
    value: number;
    unit: string;
    contextLabel: string;
    durationSec?: number | null;
    distanceM?: number | null;
  }) => void;
  onSelectPeak?: (peak: {
    kind: string;
    startSec: number;
    endSec: number;
    valueLabel: string;
    durationLabel: string;
    distanceLabel: string | null;
  } | null) => void;
  selectedPeakKey?: string | null;
}) {
  const units = useUnitSystem();
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [doneKey, setDoneKey] = useState<string | null>(null);
  // Wave 20.49cg — narrow-viewport tuning for mobile-web. Rows
  // used to run wider than the phone viewport (label 130 +
  // duration 68 + value 110 + chip + icon > 390px) and pushed
  // the page into horizontal overflow — the leading "P" of
  // "Peaks", "PEAK", "ENTIRE" was clipped off-screen. Shrink
  // min-widths + allow the row to wrap under 600px.
  const isNarrow = useMediaQuery('(max-width:600px)');
  const labelMin = isNarrow ? 96 : 130;
  const durationMin = isNarrow ? 48 : 68;
  const valueMin = isNarrow ? 84 : 110;
  const rowGap = isNarrow ? 1 : 1.5;

  const dropPeak = async (r: IntervalRow) => {
    // Wave 20.27 — When the parent provided a request-drop callback,
    // hand the intent up so the shared DropLinkDialog opens with the
    // full picker (recipients + window + visibility). Otherwise fall
    // back to the older auto-mint-with-defaults flow so any surface
    // that hasn't adopted the dialog yet still works.
    if (onRequestPeakDrop) {
      onRequestPeakDrop({
        kind: r.kind,
        value: r.value,
        unit: r.unit,
        contextLabel: peakContextLabel(r, units, isRun),
        durationSec: r.endSec - r.startSec + 1,
        distanceM: r.distanceM,
      });
      return;
    }
    const key = `${r.kind}:${r.startSec}:${r.endSec}`;
    setBusyKey(key);
    try {
      const jwt = localStorage.getItem(TOKEN_KEY);
      const res = await fetch(`${API}/peaks/share`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          kind: r.kind,
          value: r.value,
          unit: r.unit,
          contextLabel: peakContextLabel(r, units, isRun),
          durationSec: r.endSec - r.startSec + 1,
          distanceM: r.distanceM,
          displayUnits: units,
          window: '7d',
          visibility: 'public',
        }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      try {
        await navigator.clipboard.writeText(data.url);
        onDropped(`Drop link copied — ${data.url}`);
      } catch {
        onDropped(`Drop link ready: ${data.url}`);
      }
      setDoneKey(key);
    } catch (e: any) {
      onDropped(e?.message || 'Drop failed');
    } finally {
      setBusyKey(null);
    }
  };

  if (rows.length === 0) {
    return (
      <Box sx={{ mt: 3 }}>
        <Typography
          variant="overline"
          sx={{ color: colors.textMuted, letterSpacing: '0.15em' }}
        >
          Peaks in this session
        </Typography>
        <Typography sx={{ color: colors.textMuted, mt: 1, fontStyle: 'italic' }}>
          No sustained peaks detected — the metric never stayed above its
          floor for at least 20 seconds.
        </Typography>
      </Box>
    );
  }
  // Whole-run row always sits at the top (row 1 of the peaks list),
  // then sub-efforts sorted longest-duration first — a 20-min
  // sustain is more interesting than a 12-second one.
  const sorted = [...rows].sort((a, b) => {
    if (a.kind === 'peak_whole' && b.kind !== 'peak_whole') return -1;
    if (b.kind === 'peak_whole' && a.kind !== 'peak_whole') return 1;
    return b.endSec - b.startSec - (a.endSec - a.startSec);
  });
  return (
    <Box sx={{ mt: 3 }}>
      <Typography
        variant="overline"
        sx={{ color: colors.textMuted, letterSpacing: '0.15em' }}
      >
        Peaks in this session
      </Typography>
      <Stack spacing={0.5} sx={{ mt: 1 }}>
        {sorted.map((r) => {
          const key = `${r.kind}:${r.startSec}:${r.endSec}`;
          const T = r.endSec - r.startSec;
          const isSelected = selectedPeakKey === key;
          const handleRowClick = () => {
            if (!onSelectPeak) return;
            if (isSelected) {
              onSelectPeak(null);
            } else {
              onSelectPeak({
                kind: r.kind,
                startSec: r.startSec,
                endSec: r.endSec,
                valueLabel: formatPeakValue(r, units, isRun),
                durationLabel: formatDuration(T),
                distanceLabel:
                  r.distanceM != null && r.distanceM > 0
                    ? fmtPeakDistance(r.distanceM, units)
                    : null,
              });
            }
          };
          return (
            <Box
              key={key}
              onClick={handleRowClick}
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: rowGap,
                p: 1,
                borderRadius: 1.5,
                border: `1px solid ${isSelected ? colors.brand : colors.border}`,
                backgroundColor: isSelected ? 'rgba(106,255,69,0.06)' : colors.bg,
                cursor: onSelectPeak ? 'pointer' : 'default',
                transition: 'border-color 120ms, background-color 120ms',
              }}
            >
              {/* Wave 20.37 — kind label so the reader can tell
                  "Fastest 5K" apart from "Fastest 10K" apart from
                  "Fastest Half Marathon". Was implicit in the
                  duration/distance columns; explicit now. */}
              <Typography
                sx={{
                  color: colors.text,
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  minWidth: labelMin,
                  flex: '1 1 auto',
                }}
              >
                {kindLabel(r.kind, isRun)}
              </Typography>
              <Typography
                sx={{
                  color: colors.textMuted,
                  fontSize: '0.72rem',
                  minWidth: durationMin,
                  fontFamily: '"JetBrains Mono", monospace',
                }}
              >
                {formatDuration(T)}
              </Typography>
              <Typography
                sx={{
                  color: colors.text,
                  fontWeight: 800,
                  fontFamily: '"JetBrains Mono", monospace',
                  minWidth: valueMin,
                }}
              >
                {formatPeakValue(r, units, isRun)}
              </Typography>
              {r.distanceM != null && r.distanceM > 0 && (
                <Chip
                  size="small"
                  label={
                    fmtPeakDistance(r.distanceM, units)
                  }
                  sx={{
                    backgroundColor: colors.surface,
                    color: colors.textMuted,
                    fontSize: '0.65rem',
                    height: 20,
                  }}
                />
              )}
              <Box sx={{ flex: 1 }} />
              <Tooltip title={doneKey === key ? 'Copied to clipboard' : 'Drop this peak'}>
                <span>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      dropPeak(r);
                    }}
                    disabled={busyKey === key}
                    sx={{
                      color: doneKey === key ? colors.brand : colors.textMuted,
                      '&:hover': { color: colors.brand },
                    }}
                  >
                    {doneKey === key ? (
                      <CheckIcon fontSize="small" />
                    ) : (
                      <IosShareIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

const PRETTY_TARGETS: Record<string, string> = {
  '5s': '5s', '30s': '30s',
  '1min': '1min', '5min': '5min', '20min': '20min', '60min': '60min',
  '1mi': '1mi', '5k': '5K', '10k': '10K', '40k': '40K',
  'half': 'Half Marathon', 'full': 'Marathon',
};

function kindLabel(kind: string, isRun: boolean = true): string {
  // Wave 20.38 — whole-run row (always row 1) + organic sub-effort
  // rows. Sub-efforts share the same kind and are differentiated by
  // their T / D / value below the label.
  if (kind === 'peak_whole') return 'Entire session';
  if (kind === 'peak_pace') return 'Peak Pace';
  if (kind === 'peak_speed') return 'Peak Speed';
  if (kind === 'peak_power') return 'Peak Power';
  // Wave 20.49ag — cycling shows Grade-Adj SPEED to match the
  // mph/kph value format; running keeps pace.
  if (kind === 'peak_gap') return isRun ? 'Peak Grade-Adj Pace' : 'Peak Grade-Adj Speed';
  if (kind === 'peak_vam') return 'Peak Climb (VAM)';
  // Legacy exact matches from prior wave.
  if (kind === 'peak_grade_adj_speed') return 'Peak Speed';
  if (kind === 'peak_grade_adj_pace') return 'Peak Pace';
  // Wave 20.37 target-suffix kinds (shouldn't render now that the
  // backfill replaces them, but keep pretty just in case).
  const parts = kind.split('_');
  const t = (s: string | undefined): string =>
    s ? (PRETTY_TARGETS[s] ?? s) : '';
  if (parts[0] === 'peak' && parts[1] === 'power' && parts[2]) return `Best ${t(parts[2])} Power`;
  if (parts[0] === 'peak' && parts[1] === 'speed' && parts[2]) return `Fastest ${t(parts[2])}`;
  if (parts[0] === 'peak' && parts[1] === 'pace' && parts[2]) return `Fastest ${t(parts[2])}`;
  if (parts[0] === 'peak' && parts[1] === 'gap') return `Best ${t(parts[2])} GAP`;
  return kind;
}

// Wave 20.38a — Distance now respects the caller's unit system.
// Prior hardcoded km/m meant an imperial rider saw a peak they
// dropped labeled in metric on the share dialog + drop page.
function peakContextLabel(
  r: IntervalRow,
  u: 'metric' | 'imperial',
  isRun: boolean = true,
): string {
  const parts: string[] = [kindLabel(r.kind, isRun)];
  parts.push(formatDuration(r.endSec - r.startSec));
  if (r.distanceM != null && r.distanceM > 0) {
    parts.push(fmtPeakDistance(r.distanceM, u));
  }
  return parts.join(' · ');
}

function formatPeakValue(
  r: IntervalRow,
  units: 'metric' | 'imperial',
  isRun: boolean,
): string {
  switch (r.unit) {
    case 'W':
      return `${Math.round(r.value)} W`;
    case 'mps':
      return fmtSpeed(r.value, units);
    case 'sec_per_m': {
      // Wave 20.49y — running renders as min/mi | min/km,
      // cycling as mph | kph.
      const mps = r.value > 0 ? 1 / r.value : 0;
      return isRun ? fmtPace(mps, units) : fmtSpeed(mps, units);
    }
    case 'sec':
      return `${Math.round(r.value)} s`;
    case 'm':
      return fmtDistance(r.value, units);
    case 'm_per_h':
      return units === 'imperial'
        ? `${Math.round(r.value * 3.28084).toLocaleString()} ft/h`
        : `${Math.round(r.value).toLocaleString()} m/h`;
    default:
      return `${r.value}`;
  }
}

// ---------- per-tab views ----------

function PowerView({ curve, top }: { curve: CurveEntry[]; top: TopRecord[] }) {
  const units = useUnitSystem();
  const { xs, ys } = useMemo(() => downsampleForChart(curve, (e) => e.avg_power_w), [curve]);
  return (
    <>
      <Chart
        xs={xs}
        ys={ys}
        yLabel="Best avg power (W)"
        color={colors.brand}
        formatY={(v) => `${Math.round(v)} W`}
      />
      <RecordsList
        top={top}
        renderPrimary={(r) => `${Math.round(r.value)} W`}
        renderSecondary={(r) => {
          const speed = r.entry?.avg_speed_mps;
          return speed != null && speed > 0 ? fmtSpeed(speed, units) : null;
        }}
        renderDelta={(r) =>
          r.is_pr && r.prior_best > 0
            ? `PR +${Math.round(r.value - r.prior_best)} W`
            : r.is_pr
            ? 'PR'
            : null
        }
      />
    </>
  );
}

function PaceView({ curve, top }: { curve: CurveEntry[]; top: TopRecord[] }) {
  const units = useUnitSystem();
  const speedMult = units === 'imperial' ? 2.23694 : 3.6; // mph vs km/h
  const speedUnit = units === 'imperial' ? 'mph' : 'km/h';
  const { xs, ys } = useMemo(
    () =>
      downsampleForChart(curve, (e) =>
        e.avg_speed_mps != null ? e.avg_speed_mps * speedMult : null,
      ),
    [curve, speedMult],
  );
  return (
    <>
      <Chart
        xs={xs}
        ys={ys}
        yLabel={`Best avg speed (${speedUnit})`}
        color={colors.brand}
        formatY={(v) => `${v.toFixed(1)} ${speedUnit}`}
      />
      <RecordsList
        top={top}
        renderPrimary={(r) => fmtSpeed(r.value, units)}
        renderSecondary={(r) => {
          const power = r.entry?.avg_power_w;
          return power != null && power > 0 ? `${Math.round(power)} W` : null;
        }}
        renderDelta={(r) =>
          r.is_pr && r.prior_best > 0
            ? `PR +${((r.value - r.prior_best) * speedMult).toFixed(1)} ${speedUnit}`
            : r.is_pr
            ? 'PR'
            : null
        }
      />
    </>
  );
}

function AeroView({ curve, top }: { curve: AeroEntry[]; top: TopRecord[] }) {
  const units = useUnitSystem();
  const distMult = units === 'imperial' ? 1 / 1609.344 : 1 / 1000;
  const distUnit = units === 'imperial' ? 'mi' : 'km';
  // Chart the effective distance covered — the metric the user
  // reads directly ("how far you effectively moved at your held
  // effort").
  const { xs, ys } = useMemo(
    () =>
      downsampleForChart(curve, (e) => e.predicted_distance_m * distMult),
    [curve, distMult],
  );
  return (
    <>
      <Chart
        xs={xs}
        ys={ys}
        yLabel={`Projected distance (${distUnit})`}
        color={colors.brand}
        formatY={(v) => `${v.toFixed(2)} ${distUnit}`}
      />
      <RecordsList
        top={top}
        renderPrimary={(r) => fmtDistance(r.value, units)}
        renderSecondary={(r) => {
          const p = r.entry?.avg_power_w;
          const c = r.entry?.avg_cda;
          if (p == null || c == null) return null;
          return `${Math.round(p)} W · ${Number(c).toFixed(3)} CdA`;
        }}
        renderDelta={(r) =>
          r.is_pr && r.prior_best > 0
            ? `PR +${((r.value - r.prior_best) * distMult).toFixed(2)} ${distUnit}`
            : r.is_pr
            ? 'PR'
            : null
        }
      />
    </>
  );
}

// ---------- shared chart + list ----------

function Chart({
  xs,
  ys,
  yLabel,
  color,
  formatY,
}: {
  xs: number[];
  ys: (number | null)[];
  yLabel: string;
  color: string;
  formatY: (v: number) => string;
}) {
  if (xs.length === 0) {
    return (
      <Typography sx={{ color: colors.textMuted, fontStyle: 'italic', py: 4, textAlign: 'center' }}>
        No data to chart.
      </Typography>
    );
  }
  return (
    <Box sx={{ width: '100%', height: 260 }}>
      <LineChart
        height={260}
        xAxis={[
          {
            data: xs,
            label: 'Duration',
            scaleType: 'log',
            valueFormatter: (v: number) => formatDuration(v),
          },
        ]}
        yAxis={[
          {
            label: yLabel,
            valueFormatter: (v: number) => formatY(v),
          },
        ]}
        series={[
          {
            data: ys,
            color,
            showMark: false,
            curve: 'monotoneX',
            valueFormatter: (v: number | null) => (v != null ? formatY(v) : ''),
          },
        ]}
        margin={{ left: 60, right: 20, top: 10, bottom: 40 }}
      />
    </Box>
  );
}

function RecordsList({
  top,
  renderPrimary,
  renderSecondary,
  renderDelta,
}: {
  top: TopRecord[];
  renderPrimary: (r: TopRecord) => string;
  renderSecondary: (r: TopRecord) => string | null;
  renderDelta: (r: TopRecord) => string | null;
}) {
  if (!top || top.length === 0) return null;
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="overline" sx={{ color: colors.textMuted, letterSpacing: '0.15em' }}>
        Top records vs your history
      </Typography>
      <Stack spacing={0.5} sx={{ mt: 1 }}>
        {top.map((r) => (
          <Box
            key={`${r.duration_sec}`}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 1,
              borderRadius: 1.5,
              backgroundColor: r.is_pr ? `${colors.brand}12` : 'transparent',
              border: `1px solid ${r.is_pr ? `${colors.brand}44` : colors.border}`,
            }}
          >
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontWeight: 700,
                color: colors.textMuted,
                minWidth: 56,
              }}
            >
              {formatDuration(r.duration_sec)}
            </Typography>
            <Typography sx={{ fontWeight: 800, color: colors.text, minWidth: 100 }}>
              {renderPrimary(r)}
            </Typography>
            {renderSecondary(r) && (
              <Typography sx={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                · {renderSecondary(r)}
              </Typography>
            )}
            <Box sx={{ flex: 1 }} />
            {renderDelta(r) && (
              <Chip
                label={renderDelta(r)}
                size="small"
                sx={{
                  backgroundColor: r.is_pr ? colors.brand : colors.surfaceElevated,
                  color: r.is_pr ? colors.textInverse : colors.textMuted,
                  fontWeight: 800,
                  height: 22,
                  fontSize: '0.68rem',
                }}
              />
            )}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

function SessionTotals({
  totals,
  prs,
}: {
  totals: PeaksResponse['sessionTotals'];
  prs: PeaksResponse['prs'];
}) {
  const units = useUnitSystem();
  const any =
    totals.distanceM || totals.elevationGainM || totals.avgSpeedMps || totals.durationSec;
  if (!any) return null;
  return (
    <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${colors.border}` }}>
      <Typography variant="overline" sx={{ color: colors.textMuted, letterSpacing: '0.15em' }}>
        Session totals
      </Typography>
      <Stack direction="row" spacing={2} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
        <TotalTile label="Duration" value={totals.durationSec ? formatDuration(totals.durationSec) : '—'} pr={prs.durationSec} />
        <TotalTile
          label="Distance"
          value={fmtDistance(totals.distanceM, units)}
          pr={prs.distanceM}
        />
        <TotalTile
          label="Elev gain"
          value={fmtElevation(totals.elevationGainM, units)}
          pr={prs.elevationGainM}
        />
        <TotalTile
          label="Avg speed"
          value={fmtSpeed(totals.avgSpeedMps, units)}
          pr={prs.avgSpeedMps}
        />
      </Stack>
    </Box>
  );
}

function TotalTile({ label, value, pr }: { label: string; value: string; pr: boolean }) {
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        backgroundColor: pr ? `${colors.brand}12` : colors.bg,
        border: `1px solid ${pr ? `${colors.brand}44` : colors.border}`,
        minWidth: 130,
      }}
    >
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Typography
          sx={{
            fontSize: '0.65rem',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: colors.textMuted,
            fontWeight: 700,
          }}
        >
          {label}
        </Typography>
        {pr && (
          <Chip
            label="PR"
            size="small"
            sx={{
              backgroundColor: colors.brand,
              color: colors.textInverse,
              fontWeight: 800,
              height: 16,
              fontSize: '0.6rem',
            }}
          />
        )}
      </Stack>
      <Typography sx={{ fontWeight: 800, mt: 0.25, fontFamily: '"JetBrains Mono", monospace' }}>
        {value}
      </Typography>
    </Box>
  );
}

// ---------- helpers ----------

// Curves have up to ~3600 entries per session; MUI x-charts with
// log-x renders that fine but the DOM cost is real. Downsample to
// log-spaced x's for the chart while keeping the raw data intact
// for the records list.
function downsampleForChart<T extends { duration_sec: number }>(
  curve: T[],
  extractY: (e: T) => number | null,
): { xs: number[]; ys: (number | null)[] } {
  if (curve.length === 0) return { xs: [], ys: [] };
  const byD = new Map<number, T>(curve.map((c) => [c.duration_sec, c]));
  // Log-spaced sample points from min to max duration.
  const minD = curve[0].duration_sec;
  const maxD = curve[curve.length - 1].duration_sec;
  const targetPoints = 60;
  const xs: number[] = [];
  const seen = new Set<number>();
  for (let i = 0; i < targetPoints; i++) {
    const t = i / (targetPoints - 1);
    const d = Math.round(minD * Math.pow(maxD / minD, t));
    if (!seen.has(d)) {
      seen.add(d);
      xs.push(d);
    }
  }
  const ys = xs.map((d) => {
    // Nearest actual duration in the curve.
    let entry = byD.get(d);
    if (!entry) {
      let closest: T | null = null;
      let bestDist = Infinity;
      for (const c of curve) {
        const dist = Math.abs(c.duration_sec - d);
        if (dist < bestDist) {
          bestDist = dist;
          closest = c;
        }
      }
      entry = closest || undefined;
    }
    return entry ? extractY(entry) : null;
  });
  return { xs, ys };
}

function formatDuration(sec: number): string {
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (sec < 3600) return s === 0 ? `${m}m` : `${m}:${String(s).padStart(2, '0')}`;
  const h = Math.floor(sec / 3600);
  const rm = Math.floor((sec % 3600) / 60);
  return rm === 0 ? `${h}h` : `${h}h ${rm}m`;
}

function LoadingCard() {
  return (
    <Box
      sx={{
        p: 4,
        borderRadius: 3,
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <CircularProgress sx={{ color: colors.brand }} />
    </Box>
  );
}

function EmptyCard() {
  return (
    <Box
      sx={{
        p: 4,
        borderRadius: 3,
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
        textAlign: 'center',
      }}
    >
      <Typography variant="overline" sx={{ color: colors.textMuted }}>Peaks</Typography>
      <Typography sx={{ color: colors.textMuted, mt: 1 }}>
        No peak data yet — record a longer session with a power meter or GPS.
      </Typography>
    </Box>
  );
}

function ErrorCard({ message }: { message: string }) {
  return (
    <Box
      sx={{
        p: 3,
        borderRadius: 3,
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
      }}
    >
      <Typography sx={{ color: colors.danger, fontSize: '0.9rem' }}>
        Couldn't load peaks: {message}
      </Typography>
    </Box>
  );
}
